import React from 'react';
import { SaBillingInvoices } from './billing/billing-invoices.jsx';
import { SaBillingOverview } from './billing/billing-overview.jsx';
import { SaBillingSubscriptions } from './billing/billing-subscriptions.jsx';
import { saBillingCanManage } from './billing/billing-support.jsx';
import { STORES } from './data.jsx';
import { DUNNING_RULES_MOCK, FINANCIAL_HISTORY_MOCK, INVOICES_MOCK, SUBSCRIPTIONS_MOCK } from './extended-mock-data.jsx';
import { Icon } from './icons.jsx';
import { USE_REAL_BILLING_API, saIsRealBillingMode } from './mvp-config.jsx';
import { ApiErrorState, EmptyState, billingService, formatBillingStatusError, readAuthState, useBillingOverview } from './services.jsx';
import { BillingStatusBadge, CommercialStatusReasonModal, InvoiceStatusBadge, PlanBadge, TenantStatusTimeline } from './tenant-status.jsx';
// Billing & Inadimplência — Super Admin Tezus
// Módulo completo: visão geral, assinaturas, faturas, inadimplência, trials, régua, histórico

const { useState: useStateBL, useEffect: useEffectBL, useMemo: useMemoMBL, useRef: useRefBL } = React;

// ── Formatters ───────────────────────────────────────────────────────────────
const fmtBRL  = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtK    = (v) => v >= 1000 ? `R$ ${(v / 1000).toFixed(1)}k` : fmtBRL(v);
const fmtPct  = (v) => `${(v * 100).toFixed(0)}%`;

// ── Subscription Drawer ──────────────────────────────────────────────────────
const SubscriptionDrawer = ({ open, sub, onClose, onAction, onToast }) => {
  if (!open || !sub) return null;
  const rows = [
    { label: 'Plano',              value: sub.plan },
    { label: 'Valor mensal',       value: fmtBRL(sub.amount) },
    { label: 'Ciclo',              value: sub.interval === 'monthly' ? 'Mensal' : 'Anual' },
    { label: 'Status',             value: <BillingStatusBadge status={sub.status} /> },
    { label: 'Período atual',      value: `${sub.current_period_start} → ${sub.current_period_end}` },
    { label: 'Próxima cobrança',   value: sub.next_charge || '—' },
    { label: 'Trial termina em',   value: sub.trial_ends_at || '—' },
    { label: 'Cancelado em',       value: sub.cancelled_at || '—' },
    { label: 'E-mail de billing',  value: sub.billing_email },
    { label: 'Método de pagamento',value: sub.payment_method || 'Não cadastrado' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.4)', backdropFilter: 'blur(4px)', zIndex: 900, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: 440, background: 'var(--bg-elevated)', height: '100%', overflowY: 'auto', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: sub.color || '#1B3D60', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'white', flexShrink: 0 }}>
            {(sub.tenant_name || '?').split(' ').filter(w => w.length > 1).slice(0, 2).map(w => w[0]).join('')}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{sub.tenant_name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Assinatura · {sub.id}</div>
          </div>
          <button className="icon-action" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div style={{ flex: 1, padding: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 20 }}>
            {rows.map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.label}</span>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{r.value}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Ações</div>
            {[
              { label: 'Alterar plano',       icon: 'edit',    action: 'plan' },
              { label: 'Pausar cobrança',      icon: 'suspend', action: 'pause' },
              { label: 'Criar fatura manual',  icon: 'plus',    action: 'invoice' },
              { label: 'Reativar assinatura',  icon: 'play',    action: 'reactivate', hide: sub.status !== 'cancelled' && sub.status !== 'suspended' },
              { label: 'Cancelar assinatura',  icon: 'x',       action: 'cancel', danger: true },
            ].filter(a => !a.hide).map(a => (
              <button key={a.action} onClick={() => onAction(a.action, sub)} className={'btn ' + (a.danger ? 'danger' : 'ghost')} style={{ justifyContent: 'flex-start', gap: 10 }}>
                <Icon name={a.icon} size={14} />{a.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Invoice Drawer ───────────────────────────────────────────────────────────
const InvoiceDrawer = ({ open, inv, onClose, onAction }) => {
  if (!open || !inv) return null;
  const rows = [
    { label: 'Número',     value: inv.number },
    { label: 'Tenant',     value: inv.tenant_name },
    { label: 'Valor',      value: fmtBRL(inv.amount) },
    { label: 'Vencimento', value: inv.due_date },
    { label: 'Pago em',    value: inv.paid_at || '—' },
    { label: 'Status',     value: <InvoiceStatusBadge status={inv.status} /> },
    { label: 'Tentativas', value: inv.attempts },
    { label: 'Método',     value: inv.method },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.4)', backdropFilter: 'blur(4px)', zIndex: 900, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: 420, background: 'var(--bg-elevated)', height: '100%', overflowY: 'auto', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{inv.number}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inv.tenant_name}</div>
          </div>
          <button className="icon-action" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div style={{ flex: 1, padding: 24 }}>
          <div style={{ marginBottom: 20 }}>
            {rows.map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.label}</span>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{r.value}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Ações</div>
            {inv.status !== 'paid' && <button onClick={() => onAction('mark-paid', inv)} className="btn primary" style={{ justifyContent: 'flex-start', gap: 10 }}><Icon name="check" size={14} />Marcar como paga</button>}
            {(inv.status === 'overdue' || inv.status === 'open') && <button onClick={() => onAction('retry', inv)} className="btn" style={{ justifyContent: 'flex-start', gap: 10 }}><Icon name="refresh" size={14} />Reprocessar cobrança</button>}
            <button onClick={() => onAction('send', inv)} className="btn ghost" style={{ justifyContent: 'flex-start', gap: 10 }}><Icon name="mail" size={14} />Enviar por e-mail</button>
            <button onClick={() => onAction('pdf', inv)} className="btn ghost" style={{ justifyContent: 'flex-start', gap: 10 }}><Icon name="download" size={14} />Baixar PDF</button>
            {inv.status !== 'void' && <button onClick={() => onAction('void', inv)} className="btn danger" style={{ justifyContent: 'flex-start', gap: 10 }}><Icon name="x" size={14} />Cancelar fatura</button>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Tab: Visão geral ─────────────────────────────────────────────────────────
const TabBillingOverview = ({ overview }) => {
  if (!overview) return null;
  const hist = overview.mrr_history || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Gráfico MRR */}
      <div className="card" style={{ padding: 20 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>MRR — Últimos 7 meses</div>
        <div style={{ height: 120, display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          {hist.map((h, i) => {
            const maxMrr = Math.max(...hist.map(x => x.mrr));
            const pct    = (h.mrr / maxMrr) * 100;
            const isLast = i === hist.length - 1;
            return (
              <div key={h.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: isLast ? 'var(--brand-navy)' : 'var(--text-muted)' }}>{fmtK(h.mrr)}</div>
                <div style={{ width: '100%', height: `${Math.max(pct, 8)}%`, background: isLast ? 'var(--brand-navy)' : 'var(--border-strong)', borderRadius: '4px 4px 0 0', transition: 'height 600ms ease', minHeight: 10 }} />
                <div style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h.month}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Métricas chave */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {[
          { label: 'Tenants inadimplentes', value: overview.overdue_tenants, sub: fmtBRL(overview.overdue_revenue) + ' vencido', color: 'var(--danger)' },
          { label: 'Trials ativos', value: overview.active_trials, sub: fmtPct(overview.trial_conversion_rate) + ' de conversão', color: 'var(--info)' },
          { label: 'Faturas vencidas', value: overview.invoices_overdue, sub: overview.invoices_due_today + ' vencem hoje', color: 'var(--warning)' },
        ].map(m => (
          <div key={m.label} className="card" style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{m.label}</div>
            <div style={{ fontSize: 32, fontWeight: 700, color: m.color, letterSpacing: '-0.02em', marginBottom: 2 }}>{m.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Tab: Assinaturas ─────────────────────────────────────────────────────────
const TabAssinaturas = ({ subs, onToast }) => {
  const [selected, setSelected] = useStateBL(null);
  const [query,    setQuery]    = useStateBL('');
  const [filter,   setFilter]   = useStateBL('all');

  const filtered = useMemoMBL(() => {
    let r = subs;
    if (filter !== 'all') r = r.filter(s => s.status === filter);
    if (query.trim()) { const q = query.toLowerCase(); r = r.filter(s => (s.tenant_name || '').toLowerCase().includes(q)); }
    return r;
  }, [subs, filter, query]);

  const handleAction = (action, sub) => {
    const msgs = { plan: 'Abrir alteração de plano', pause: `Cobrança de ${sub.tenant_name} pausada`, invoice: 'Drawer de fatura manual em breve', cancel: `Assinatura de ${sub.tenant_name} cancelada`, reactivate: `${sub.tenant_name} reativada` };
    onToast(msgs[action] || action);
    setSelected(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 auto', maxWidth: 280 }}>
          <Icon name="search" size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Buscar tenant..." style={{ width: '100%', height: 34, paddingLeft: 34, paddingRight: 12, border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--bg)', color: 'var(--text)' }} />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[{ id: 'all', label: 'Todas' }, { id: 'active', label: 'Ativas' }, { id: 'trialing', label: 'Trial' }, { id: 'past_due', label: 'Em atraso' }, { id: 'unpaid', label: 'Inadimplente' }].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} style={{ padding: '5px 11px', borderRadius: 8, border: 'none', background: filter === f.id ? 'var(--brand-navy)' : 'var(--bg-subtle)', color: filter === f.id ? 'white' : 'var(--text-secondary)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'var(--bg-subtle)' }}>
            <tr>{['Tenant', 'Plano', 'Status', 'Valor', 'Próxima cobrança', 'Trial termina', 'Método', 'Ações'].map(h => (
              <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {filtered.map(s => (
              <tr key={s.id} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => setSelected(s)} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ padding: '11px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: s.color || '#1B3D60', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                      {(s.tenant_name || '').split(' ').filter(w => w.length > 1).slice(0, 2).map(w => w[0]).join('')}
                    </div>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{s.tenant_name}</div>
                  </div>
                </td>
                <td style={{ padding: '11px 14px' }}><PlanBadge plan={s.plan_id} /></td>
                <td style={{ padding: '11px 14px' }}><BillingStatusBadge status={s.status} /></td>
                <td style={{ padding: '11px 14px', fontWeight: 600, fontSize: 13 }}>{fmtBRL(s.amount)}</td>
                <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--text-secondary)' }}>{s.next_charge || '—'}</td>
                <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--text-secondary)' }}>{s.trial_ends_at || '—'}</td>
                <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{s.payment_method || <span style={{ color: 'var(--danger)', fontWeight: 600, fontSize: 11 }}>Não cadastrado</span>}</td>
                <td style={{ padding: '11px 14px' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="icon-action" title="Ver detalhes" onClick={() => setSelected(s)}><Icon name="more" size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <EmptyState icon="cardCoin" title="Nenhuma assinatura encontrada" />}
      </div>
      <SubscriptionDrawer open={!!selected} sub={selected} onClose={() => setSelected(null)} onAction={handleAction} onToast={onToast} />
    </div>
  );
};

// ── Tab: Faturas ─────────────────────────────────────────────────────────────
const TabFaturas = ({ invoices, onToast }) => {
  const [selected, setSelected] = useStateBL(null);
  const [filter,   setFilter]   = useStateBL('all');
  const [loading,  setLoading]  = useStateBL(false);

  const filtered = filter === 'all' ? invoices : invoices.filter(i => i.status === filter);

  const handleAction = async (action, inv) => {
    setSelected(null);
    if (action === 'retry') {
      setLoading(true);
      try {
        await billingService.retryPayment(inv.id);
        onToast(`Cobrança processada para ${inv.tenant_name}`);
      } catch (e) {
        onToast(`Erro: ${e.message}`);
      } finally { setLoading(false); }
    } else if (action === 'mark-paid') {
      onToast(`Fatura ${inv.number} marcada como paga`);
    } else if (action === 'send') {
      onToast(`Fatura enviada para ${inv.tenant_name}`);
    } else if (action === 'void') {
      onToast(`Fatura ${inv.number} cancelada`);
    } else {
      onToast(action + ' — ' + inv.number);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {[{ id: 'all', label: 'Todas' }, { id: 'paid', label: 'Pagas' }, { id: 'open', label: 'Em aberto' }, { id: 'overdue', label: 'Vencidas' }, { id: 'uncollectible', label: 'Incobráveis' }].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} style={{ padding: '5px 11px', borderRadius: 8, border: 'none', background: filter === f.id ? 'var(--brand-navy)' : 'var(--bg-subtle)', color: filter === f.id ? 'white' : 'var(--text-secondary)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
            {f.label}
          </button>
        ))}
      </div>
      <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'var(--bg-subtle)' }}>
            <tr>{['Número', 'Tenant', 'Valor', 'Vencimento', 'Status', 'Tentativas', 'Método', 'Pago em', 'Ações'].map(h => (
              <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {filtered.map(inv => (
              <tr key={inv.id} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => setSelected(inv)} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ padding: '10px 14px', fontSize: 12, fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-secondary)' }}>{inv.number}</td>
                <td style={{ padding: '10px 14px', fontWeight: 500, fontSize: 13 }}>{inv.tenant_name}</td>
                <td style={{ padding: '10px 14px', fontWeight: 600, fontSize: 13 }}>{fmtBRL(inv.amount)}</td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)' }}>{inv.due_date}</td>
                <td style={{ padding: '10px 14px' }}><InvoiceStatusBadge status={inv.status} /></td>
                <td style={{ padding: '10px 14px', fontSize: 13, textAlign: 'center' }}>
                  <span style={{ fontWeight: inv.attempts > 2 ? 700 : 400, color: inv.attempts > 2 ? 'var(--danger)' : 'var(--text-secondary)' }}>{inv.attempts}</span>
                </td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{inv.method}</td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{inv.paid_at || '—'}</td>
                <td style={{ padding: '10px 14px' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {inv.status !== 'paid' && <button className="icon-action" title="Reprocessar" onClick={() => handleAction('retry', inv)} disabled={loading}><Icon name="refresh" size={13} /></button>}
                    <button className="icon-action" title="Ver detalhes" onClick={() => setSelected(inv)}><Icon name="more" size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <EmptyState icon="cardCoin" title="Nenhuma fatura encontrada" />}
      </div>
      <InvoiceDrawer open={!!selected} inv={selected} onClose={() => setSelected(null)} onAction={handleAction} />
    </div>
  );
};

// ── Modal: Enviar Lembrete ─────────────────────────────────────────────────
const SendReminderModalBL = ({ open, item, onClose, onConfirm }) => {
  const [channel, setChannel] = useStateBL('email');
  const [message, setMessage] = useStateBL('Identificamos uma fatura em aberto em sua conta. Por favor, regularize para evitar a suspensão dos serviços.');
  const [sending, setSending] = useStateBL(false);
  const [sent,    setSent]    = useStateBL(false);

  useEffectBL(() => { if (!open) { setSending(false); setSent(false); setChannel('email'); } }, [open]);
  if (!open || !item) return null;

  const CHANNELS = [
    { id: 'email',    label: 'E-mail',         icon: 'mail' },
    { id: 'whatsapp', label: 'WhatsApp (mock)', icon: 'messageCircle' },
    { id: 'ambos',    label: 'Ambos',           icon: 'zap' },
  ];

  const handleSend = () => {
    setSending(true);
    setTimeout(() => { setSending(false); setSent(true); onConfirm && onConfirm({ item, channel, message }); }, 1200);
  };

  return (
    <div onClick={sent ? undefined : onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.44)', backdropFilter: 'blur(4px)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 480, background: 'var(--bg-elevated)', borderRadius: 16, boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--info-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="mail" size={16} style={{ color: 'var(--info)' }} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Enviar lembrete</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{item.tenant_name} · {fmtBRL(item.amount)} em atraso</div>
          </div>
          {!sending && !sent && <button className="icon-action" onClick={onClose}><Icon name="x" size={15} /></button>}
        </div>
        <div style={{ padding: '18px 22px' }}>
          {!sent ? (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Canal de envio</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {CHANNELS.map(c => (
                    <button key={c.id} onClick={() => setChannel(c.id)} style={{ flex: 1, padding: '9px 8px', borderRadius: 8, border: `1.5px solid ${channel === c.id ? 'var(--brand-navy)' : 'var(--border)'}`, background: channel === c.id ? 'color-mix(in srgb, var(--brand-navy) 8%, var(--bg-elevated))' : 'var(--bg-elevated)', color: channel === c.id ? 'var(--brand-navy)' : 'var(--text-secondary)', fontSize: 12, fontWeight: channel === c.id ? 600 : 400, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, transition: 'all 120ms' }}>
                      <Icon name={c.icon} size={14} />{c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Mensagem</div>
                <textarea value={message} onChange={e => setMessage(e.target.value)} style={{ width: '100%', minHeight: 80, padding: '8px 12px', border: '1px solid var(--border-strong)', borderRadius: 8, background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5 }} />
              </div>
              <div style={{ padding: '9px 12px', background: 'var(--bg-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 8, alignItems: 'center' }}>
                <Icon name="mail" size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                E-mail de billing: <strong>{item.billing_email}</strong>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <Icon name="check" size={22} style={{ color: 'var(--success)' }} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Lembrete enviado!</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6 }}>Via {CHANNELS.find(c => c.id === channel)?.label} para {item.tenant_name}</div>
            </div>
          )}
        </div>
        <div style={{ padding: '12px 22px', borderTop: '1px solid var(--border)', background: 'var(--bg-subtle)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {!sent && !sending && <button className="btn ghost" onClick={onClose}>Cancelar</button>}
          {!sent && <button className="btn primary" onClick={handleSend} disabled={sending} style={{ minWidth: 130 }}>{sending ? <><div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid rgba(255,255,255,.3)', borderTop: '2px solid white', animation: 'spin 0.8s linear infinite' }} />Enviando…</> : <><Icon name="mail" size={13} />Enviar lembrete</>}</button>}
          {sent && <button className="btn primary" onClick={onClose}>Fechar</button>}
        </div>
      </div>
    </div>
  );
};

// ── Modal: Reprocessar Cobrança ──────────────────────────────────────────────
const ReprocessChargeModalBL = ({ open, item, onClose, onConfirm }) => {
  const [step, setStep] = useStateBL(0);

  useEffectBL(() => { if (!open) setStep(0); }, [open]);
  if (!open || !item) return null;

  const handleConfirm = () => {
    setStep(1);
    setTimeout(() => { setStep(2); onConfirm && onConfirm({ item }); }, 2400);
  };

  return (
    <div onClick={step > 0 ? undefined : onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.44)', backdropFilter: 'blur(4px)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 440, background: 'var(--bg-elevated)', borderRadius: 16, boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--warning-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="refresh" size={16} style={{ color: 'var(--warning)' }} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Reprocessar cobrança</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{item.tenant_name} · {fmtBRL(item.amount)}</div>
          </div>
          {step === 0 && <button className="icon-action" onClick={onClose}><Icon name="x" size={15} /></button>}
        </div>
        <div style={{ padding: '18px 22px' }}>
          {step === 0 && (
            <>
              <div style={{ padding: '10px 12px', background: 'var(--warning-bg)', borderRadius: 8, fontSize: 13, color: 'var(--warning)', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <Icon name="alert" size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                O sistema tentará capturar o pagamento novamente usando o método de pagamento cadastrado.
              </div>
              {[['Tenant', item.tenant_name], ['Valor', fmtBRL(item.amount)], ['Status atual', item.status || 'past_due'], ['E-mail de billing', item.billing_email]].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{k}</span>
                  <span style={{ fontWeight: 500 }}>{v}</span>
                </div>
              ))}
            </>
          )}
          {step === 1 && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid var(--border)', borderTop: '3px solid var(--brand-navy)', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
              <div style={{ fontWeight: 600, fontSize: 14 }}>Reprocessando cobrança…</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 14 }}>
                {['Conectando ao gateway…', 'Tentando captura do pagamento…', 'Atualizando status da fatura…'].map((s, i) => (
                  <div key={i} style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 7 }}><div style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--brand-navy)', opacity: 0.5 + i * 0.2 }} />{s}</div>
                ))}
              </div>
            </div>
          )}
          {step === 2 && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}><Icon name="check" size={22} style={{ color: 'var(--success)' }} /></div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Cobrança reprocessada</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6 }}>Status atualizado para <strong>Pendente</strong>. Aguarde confirmação do gateway.</div>
            </div>
          )}
        </div>
        <div style={{ padding: '12px 22px', borderTop: '1px solid var(--border)', background: 'var(--bg-subtle)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {step === 0 && <><button className="btn ghost" onClick={onClose}>Cancelar</button><button className="btn primary" onClick={handleConfirm}><Icon name="refresh" size={13} />Reprocessar</button></>}
          {step === 2 && <button className="btn primary" onClick={onClose}><Icon name="check" size={13} />Concluído</button>}
        </div>
      </div>
    </div>
  );
};

// ── Modal: Suspender Tenant ──────────────────────────────────────────────────
const SuspendTenantModalBL = ({ open, item, onClose, onConfirm }) => {
  const [confirmText, setConfirmText] = useStateBL('');
  const [running,     setRunning]     = useStateBL(false);
  const [done,        setDone]        = useStateBL(false);

  useEffectBL(() => { if (!open) { setConfirmText(''); setRunning(false); setDone(false); } }, [open]);
  if (!open || !item) return null;

  const canSubmit = confirmText === 'SUSPENDER';

  const handleSuspend = () => {
    if (!canSubmit) return;
    setRunning(true);
    setTimeout(() => { setRunning(false); setDone(true); onConfirm && onConfirm({ item }); }, 1600);
  };

  return (
    <div onClick={done || running ? undefined : onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', backdropFilter: 'blur(4px)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 460, background: 'var(--bg-elevated)', borderRadius: 16, boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--danger-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="suspend" size={16} style={{ color: 'var(--danger)' }} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Suspender tenant</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{item.tenant_name} — ação sensível</div>
          </div>
          {!running && !done && <button className="icon-action" onClick={onClose}><Icon name="x" size={15} /></button>}
        </div>
        <div style={{ padding: '18px 22px' }}>
          {!done && !running && (
            <>
              <div style={{ padding: '12px 14px', background: 'var(--danger-bg)', borderRadius: 8, marginBottom: 16, border: '1px solid color-mix(in srgb, var(--danger) 20%, transparent)' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--danger)', marginBottom: 4 }}>Impacto desta ação</div>
                <div style={{ fontSize: 13, color: 'var(--danger)', lineHeight: 1.6 }}>O tenant terá acesso bloqueado até regularização.<br/>• Loja desativada para clientes finais<br/>• Acesso ao painel bloqueado<br/>• Integrações suspensas</div>
              </div>
              {[['Tenant', item.tenant_name], ['Valor em atraso', fmtBRL(item.amount)]].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{k}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
              <div style={{ marginTop: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Digite <code style={{ fontFamily: 'JetBrains Mono, monospace', background: 'var(--danger-bg)', color: 'var(--danger)', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>SUSPENDER</code> para confirmar</label>
                <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="SUSPENDER" style={{ width: '100%', height: 36, padding: '0 12px', borderRadius: 8, border: `1.5px solid ${canSubmit ? 'var(--danger)' : 'var(--border-strong)'}`, background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em', transition: 'border-color 150ms' }} />
              </div>
            </>
          )}
          {running && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid var(--border)', borderTop: '3px solid var(--danger)', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
              <div style={{ fontWeight: 600 }}>Suspendendo tenant…</div>
            </div>
          )}
          {done && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--danger-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}><Icon name="suspend" size={22} style={{ color: 'var(--danger)' }} /></div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Tenant suspenso</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6 }}>{item.tenant_name} — acesso bloqueado até regularização</div>
            </div>
          )}
        </div>
        <div style={{ padding: '12px 22px', borderTop: '1px solid var(--border)', background: 'var(--bg-subtle)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {!done && !running && <><button className="btn ghost" onClick={onClose}>Cancelar</button><button className="btn danger" disabled={!canSubmit} onClick={handleSuspend} style={{ opacity: canSubmit ? 1 : 0.45 }}><Icon name="suspend" size={13} />Suspender tenant</button></>}
          {done && <button className="btn primary" onClick={onClose}><Icon name="check" size={13} />Concluído</button>}
        </div>
      </div>
    </div>
  );
};

// ── Tab: Inadimplência ───────────────────────────────────────────────────────
const TabInadimplencia = ({ subs, onToast, onNav }) => {
  const overdueItems = subs.filter(s => s.status === 'past_due' || s.status === 'unpaid');
  const [reminderItem,  setReminderItem]  = useStateBL(null);
  const [reprocessItem, setReprocessItem] = useStateBL(null);
  const [suspendItem,   setSuspendItem]   = useStateBL(null);
  const [localStates,   setLocalStates]   = useStateBL({});

  const addHistory = (event, tenant, amount = 0) => {
    const ts = 'Hoje, ' + new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    if (Array.isArray(FINANCIAL_HISTORY_MOCK)) {
      FINANCIAL_HISTORY_MOCK.unshift({ id: 'fh_' + Date.now(), date: ts, tenant, event, amount, actor: 'Admin', origin: 'Super Admin', result: 'success' });
    }
  };

  const DaysBadge = ({ days }) => {
    const color = days >= 30 ? '#EC2A58' : days >= 15 ? '#b45309' : '#8a94a6';
    const bg    = days >= 30 ? '#fdecf0' : days >= 15 ? '#fef3c7' : '#f4f5f7';
    const label = days >= 30 ? '30+ dias' : days >= 15 ? '15-30 dias' : days >= 8 ? '8-15 dias' : '1-7 dias';
    return <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 20, background: bg, color, fontSize: 11, fontWeight: 700 }}>{label}</span>;
  };

  return (
    <div>
      {overdueItems.length > 0 && (
        <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name="alert" size={18} style={{ color: 'var(--danger)', flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: 'var(--danger)', fontWeight: 500 }}>
            <strong>{overdueItems.length}</strong> tenant{overdueItems.length > 1 ? 's' : ''} com fatura(s) em atraso · {fmtBRL(overdueItems.reduce((a, s) => a + s.amount, 0))} em risco
          </span>
        </div>
      )}
      <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'var(--bg-subtle)' }}>
            <tr>{['Tenant', 'Plano', 'Valor vencido', 'Dias em atraso', 'Status billing', 'Ações'].map(h => (
              <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {overdueItems.map(s => {
              const store = (STORES || []).find(t => t.tenant_id == s.tenant_id || t.id === s.id);
              const daysOverdue = store?.days_overdue || 0;
              return (
                <tr key={s.id} style={{ borderTop: '1px solid var(--border)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 6, background: s.color || '#1B3D60', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                        {(s.tenant_name || '').split(' ').filter(w => w.length > 1).slice(0, 2).map(w => w[0]).join('')}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{s.tenant_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.billing_email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '11px 14px' }}><PlanBadge plan={s.plan_id} /></td>
                  <td style={{ padding: '11px 14px', fontWeight: 700, color: 'var(--danger)', fontSize: 14 }}>{fmtBRL(s.amount)}</td>
                  <td style={{ padding: '11px 14px' }}>{daysOverdue > 0 ? <DaysBadge days={daysOverdue} /> : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <BillingStatusBadge status={localStates[s.id]?.status || s.status} />
                    {localStates[s.id]?.label && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3, fontStyle: 'italic' }}>{localStates[s.id].label}</div>}
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="icon-action" title="Enviar lembrete" onClick={() => setReminderItem(s)}><Icon name="mail" size={13} /></button>
                      <button className="icon-action" title="Reprocessar cobrança" onClick={() => setReprocessItem(s)}><Icon name="refresh" size={13} /></button>
                      <button className="icon-action danger" title="Suspender tenant" onClick={() => setSuspendItem(s)}><Icon name="suspend" size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {overdueItems.length === 0 && <EmptyState icon="check" title="Sem inadimplência" description="Todos os tenants estão com pagamentos em dia." />}
      </div>

      <SendReminderModalBL
        open={!!reminderItem} item={reminderItem} onClose={() => setReminderItem(null)}
        onConfirm={({ item, channel }) => {
          const ts = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
          const chLabel = channel === 'email' ? 'E-mail' : channel === 'whatsapp' ? 'WhatsApp' : 'E-mail + WhatsApp';
          setLocalStates(p => ({ ...p, [item.id]: { label: `Lembrete enviado (${chLabel}) · ${ts}` } }));
          addHistory(`Lembrete enviado via ${chLabel}`, item.tenant_name, item.amount);
          onToast && onToast(`Lembrete enviado para ${item.tenant_name}`);
        }}
      />
      <ReprocessChargeModalBL
        open={!!reprocessItem} item={reprocessItem} onClose={() => setReprocessItem(null)}
        onConfirm={({ item }) => {
          const ts = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
          setLocalStates(p => ({ ...p, [item.id]: { status: 'pending', label: `Reprocessada · ${ts}` } }));
          addHistory('Cobrança reprocessada manualmente', item.tenant_name, item.amount);
          onToast && onToast(`Cobrança de ${item.tenant_name} reprocessada`);
        }}
      />
      <SuspendTenantModalBL
        open={!!suspendItem} item={suspendItem} onClose={() => setSuspendItem(null)}
        onConfirm={({ item }) => {
          setLocalStates(p => ({ ...p, [item.id]: { status: 'suspended', label: 'Suspenso agora' } }));
          addHistory('Tenant suspenso por inadimplência', item.tenant_name, item.amount);
          onToast && onToast(`${item.tenant_name} suspenso com sucesso`);
        }}
      />
    </div>
  );
};

// ── Modal: Converter Trial para Pago ────────────────────────────────────────
const ConvertTrialModal = ({ open, trial, onClose, onConfirm }) => {
  const PLANS = [
    { id: 'basic', label: 'Basic', monthly: 99, annual: 990 },
    { id: 'pro', label: 'Pro', monthly: 199, annual: 1990 },
    { id: 'enterprise', label: 'Enterprise', monthly: 499, annual: 4990 },
  ];
  const [selPlan,    setSelPlan]    = useStateBL('pro');
  const [cycle,      setCycle]      = useStateBL('monthly');
  const [sendEmail,  setSendEmail]  = useStateBL(true);
  const [step,       setStep]       = useStateBL(0);
  useEffectBL(() => { if (!open) { setStep(0); setSelPlan('pro'); setCycle('monthly'); setSendEmail(true); } }, [open]);
  if (!open || !trial) return null;
  const plan  = PLANS.find(p => p.id === selPlan) || PLANS[1];
  const value = cycle === 'monthly' ? plan.monthly : plan.annual;
  const startDate = new Date(); startDate.setDate(startDate.getDate() + 1);
  const startStr  = startDate.toLocaleDateString('pt-BR');
  const handleConfirm = () => { setStep(1); setTimeout(() => { setStep(2); onConfirm && onConfirm({ trial, plan, cycle, sendEmail }); }, 1800); };
  return (
    <div onClick={step > 0 ? undefined : onClose} style={{ position:'fixed',inset:0,background:'rgba(10,22,40,0.44)',backdropFilter:'blur(4px)',zIndex:900,display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width:500,background:'var(--bg-elevated)',borderRadius:16,boxShadow:'var(--shadow-lg)',border:'1px solid var(--border)',overflow:'hidden' }}>
        <div style={{ padding:'18px 22px 14px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12 }}>
          <div style={{ width:36,height:36,borderRadius:8,background:'var(--success-bg)',display:'flex',alignItems:'center',justifyContent:'center' }}><Icon name="check" size={16} style={{ color:'var(--success)' }}/></div>
          <div style={{ flex:1 }}><div style={{ fontWeight:700,fontSize:15 }}>Converter para pago</div><div style={{ fontSize:12,color:'var(--text-muted)' }}>{trial.tenant_name}</div></div>
          {step === 0 && <button className="icon-action" onClick={onClose}><Icon name="x" size={15}/></button>}
        </div>
        <div style={{ padding:'18px 22px' }}>
          {step === 0 && <>
            <div style={{ marginBottom:16 }}>
              {[['Tenant / Loja', trial.tenant_name], ['Plano atual (trial)', trial.plan || trial.plan_id], ['Método de pagamento', trial.payment_method || '—'], ['Início da cobrança', startStr]].map(([k, v]) => (
                <div key={k} style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 0',borderBottom:'1px solid var(--border)',fontSize:13 }}>
                  <span style={{ color:'var(--text-muted)',fontSize:12 }}>{k}</span>
                  <span style={{ fontWeight:500, color: k==='Método de pagamento' && !trial.payment_method ? 'var(--danger)' : 'inherit' }}>{v}</span>
                </div>
              ))}
            </div>
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:8 }}>Plano para conversão</div>
              <div style={{ display:'flex',gap:6 }}>
                {PLANS.map(p => (
                  <button key={p.id} onClick={() => setSelPlan(p.id)} style={{ flex:1,padding:'9px 6px',borderRadius:8,border:`1.5px solid ${selPlan===p.id?'var(--brand-navy)':'var(--border)'}`,background:selPlan===p.id?'color-mix(in srgb,var(--brand-navy) 8%,var(--bg-elevated))':'var(--bg-elevated)',color:selPlan===p.id?'var(--brand-navy)':'var(--text-secondary)',fontSize:12,fontWeight:selPlan===p.id?700:400,cursor:'pointer',transition:'all 120ms' }}>{p.label}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:8 }}>Ciclo de cobrança</div>
              <div style={{ display:'flex',gap:6 }}>
                {[['monthly', `Mensal · ${fmtBRL(plan.monthly)}`], ['annual', `Anual · ${fmtBRL(plan.annual)}`]].map(([v, l]) => (
                  <button key={v} onClick={() => setCycle(v)} style={{ flex:1,padding:'8px 6px',borderRadius:8,border:`1.5px solid ${cycle===v?'var(--brand-navy)':'var(--border)'}`,background:cycle===v?'color-mix(in srgb,var(--brand-navy) 8%,var(--bg-elevated))':'var(--bg-elevated)',color:cycle===v?'var(--brand-navy)':'var(--text-secondary)',fontSize:12,fontWeight:cycle===v?600:400,cursor:'pointer',transition:'all 120ms' }}>{l}</button>
                ))}
              </div>
            </div>
            <div style={{ padding:'10px 14px',background:'var(--success-bg)',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14 }}>
              <span style={{ fontSize:13,color:'var(--success)',fontWeight:500 }}>Valor a cobrar</span>
              <span style={{ fontSize:18,fontWeight:700,color:'var(--success)' }}>{fmtBRL(value)}/{cycle==='monthly'?'mês':'ano'}</span>
            </div>
            <label style={{ display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13 }}>
              <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} style={{ accentColor:'var(--brand-navy)',width:14,height:14 }}/>
              <span style={{ color:'var(--text-secondary)' }}>Enviar e-mail de confirmação ao cliente</span>
            </label>
          </>}
          {step === 1 && <div style={{ textAlign:'center',padding:'28px 0' }}>
            <div style={{ width:44,height:44,borderRadius:'50%',border:'3px solid var(--border)',borderTop:'3px solid var(--brand-navy)',animation:'spin 0.8s linear infinite',margin:'0 auto 16px' }}/>
            <div style={{ fontWeight:600,fontSize:14 }}>Convertendo para pago…</div>
            <div style={{ fontSize:12,color:'var(--text-muted)',marginTop:8 }}>Ativando plano {plan.label} e notificando gateway</div>
          </div>}
          {step === 2 && <div style={{ textAlign:'center',padding:'28px 0' }}>
            <div style={{ width:52,height:52,borderRadius:'50%',background:'var(--success-bg)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px' }}><Icon name="check" size={22} style={{ color:'var(--success)' }}/></div>
            <div style={{ fontWeight:700,fontSize:15 }}>Trial convertido!</div>
            <div style={{ fontSize:13,color:'var(--text-muted)',marginTop:6 }}>{trial.tenant_name} · Plano {plan.label} · {cycle==='monthly'?'Mensal':'Anual'}</div>
          </div>}
        </div>
        <div style={{ padding:'12px 22px',borderTop:'1px solid var(--border)',background:'var(--bg-subtle)',display:'flex',gap:8,justifyContent:'flex-end' }}>
          {step === 0 && <><button className="btn ghost" onClick={onClose}>Cancelar</button><button className="btn primary" onClick={handleConfirm}><Icon name="check" size={13}/>Converter para pago</button></>}
          {step === 2 && <button className="btn primary" onClick={onClose}><Icon name="check" size={13}/>Concluído</button>}
        </div>
      </div>
    </div>
  );
};

// ── Modal: Estender Trial ────────────────────────────────────────────────────
const ExtendTrialModal = ({ open, trial, onClose, onConfirm }) => {
  const [days,   setDays]   = useStateBL('7');
  const [custom, setCustom] = useStateBL('');
  const [reason, setReason] = useStateBL('');
  const [notify, setNotify] = useStateBL(true);
  const [step,   setStep]   = useStateBL(0);
  useEffectBL(() => { if (!open) { setStep(0); setDays('7'); setCustom(''); setReason(''); setNotify(true); } }, [open]);
  if (!open || !trial) return null;
  const daysNum = days === 'custom' ? (parseInt(custom) || 0) : parseInt(days);
  const newExpiry = () => {
    try {
      const base = trial.trial_ends_at ? new Date(trial.trial_ends_at) : new Date();
      if (isNaN(base.getTime())) { const d = new Date(); d.setDate(d.getDate() + daysNum); return d.toLocaleDateString('pt-BR'); }
      base.setDate(base.getDate() + daysNum);
      return base.toLocaleDateString('pt-BR');
    } catch (e) { return '—'; }
  };
  const handleConfirm = () => {
    if (!daysNum || daysNum < 1) return;
    setStep(1);
    setTimeout(() => { setStep(2); onConfirm && onConfirm({ trial, days: daysNum, reason, notify }); }, 1200);
  };
  return (
    <div onClick={step > 0 ? undefined : onClose} style={{ position:'fixed',inset:0,background:'rgba(10,22,40,0.44)',backdropFilter:'blur(4px)',zIndex:900,display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width:460,background:'var(--bg-elevated)',borderRadius:16,boxShadow:'var(--shadow-lg)',border:'1px solid var(--border)',overflow:'hidden' }}>
        <div style={{ padding:'18px 22px 14px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12 }}>
          <div style={{ width:36,height:36,borderRadius:8,background:'var(--info-bg)',display:'flex',alignItems:'center',justifyContent:'center' }}><Icon name="plus" size={16} style={{ color:'var(--info)' }}/></div>
          <div style={{ flex:1 }}><div style={{ fontWeight:700,fontSize:15 }}>Estender trial</div><div style={{ fontSize:12,color:'var(--text-muted)' }}>{trial.tenant_name}</div></div>
          {step === 0 && <button className="icon-action" onClick={onClose}><Icon name="x" size={15}/></button>}
        </div>
        <div style={{ padding:'18px 22px' }}>
          {step === 0 && <>
            <div style={{ display:'flex',justifyContent:'space-between',padding:'9px 0',borderBottom:'1px solid var(--border)',fontSize:13,marginBottom:16 }}>
              <span style={{ color:'var(--text-muted)',fontSize:12 }}>Expiração atual</span>
              <span style={{ fontWeight:600 }}>{trial.trial_ends_at || '—'}</span>
            </div>
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:8 }}>Dias a estender</div>
              <div style={{ display:'flex',gap:6 }}>
                {[['7','7 dias'],['14','14 dias'],['30','30 dias'],['custom','Personalizado']].map(([v, l]) => (
                  <button key={v} onClick={() => setDays(v)} style={{ flex:1,padding:'8px 4px',borderRadius:8,border:`1.5px solid ${days===v?'var(--brand-navy)':'var(--border)'}`,background:days===v?'color-mix(in srgb,var(--brand-navy) 8%,var(--bg-elevated))':'var(--bg-elevated)',color:days===v?'var(--brand-navy)':'var(--text-secondary)',fontSize:11,fontWeight:days===v?700:400,cursor:'pointer',transition:'all 120ms' }}>{l}</button>
                ))}
              </div>
              {days === 'custom' && <input type="number" min="1" max="365" value={custom} onChange={e => setCustom(e.target.value)} placeholder="Nº de dias" style={{ marginTop:8,width:'100%',height:34,padding:'0 12px',borderRadius:8,border:'1px solid var(--border-strong)',background:'var(--bg-elevated)',color:'var(--text)',fontSize:13,fontFamily:'inherit' }}/>}
            </div>
            {daysNum > 0 && <div style={{ padding:'10px 14px',background:'var(--info-bg)',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,fontSize:13 }}>
              <span style={{ color:'var(--info)' }}>Nova data de expiração</span>
              <strong style={{ color:'var(--info)' }}>{newExpiry()}</strong>
            </div>}
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:6 }}>Motivo</div>
              <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Ex.: Cliente solicitou mais tempo para avaliar integração com Bling…" style={{ width:'100%',minHeight:64,padding:'8px 12px',border:'1px solid var(--border-strong)',borderRadius:8,background:'var(--bg-elevated)',color:'var(--text)',fontSize:13,fontFamily:'inherit',resize:'vertical',lineHeight:1.5 }}/>
            </div>
            <label style={{ display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13 }}>
              <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} style={{ accentColor:'var(--brand-navy)',width:14,height:14 }}/>
              <span style={{ color:'var(--text-secondary)' }}>Notificar cliente sobre a extensão</span>
            </label>
          </>}
          {step === 1 && <div style={{ textAlign:'center',padding:'28px 0' }}>
            <div style={{ width:44,height:44,borderRadius:'50%',border:'3px solid var(--border)',borderTop:'3px solid var(--brand-navy)',animation:'spin 0.8s linear infinite',margin:'0 auto 16px' }}/>
            <div style={{ fontWeight:600 }}>Estendendo trial…</div>
          </div>}
          {step === 2 && <div style={{ textAlign:'center',padding:'28px 0' }}>
            <div style={{ width:52,height:52,borderRadius:'50%',background:'var(--success-bg)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px' }}><Icon name="check" size={22} style={{ color:'var(--success)' }}/></div>
            <div style={{ fontWeight:700,fontSize:15 }}>Trial estendido!</div>
            <div style={{ fontSize:13,color:'var(--text-muted)',marginTop:6 }}>Nova expiração: <strong>{newExpiry()}</strong></div>
          </div>}
        </div>
        <div style={{ padding:'12px 22px',borderTop:'1px solid var(--border)',background:'var(--bg-subtle)',display:'flex',gap:8,justifyContent:'flex-end' }}>
          {step === 0 && <><button className="btn ghost" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={!daysNum||daysNum<1} onClick={handleConfirm} style={{ opacity:daysNum&&daysNum>0?1:0.5 }}><Icon name="plus" size={13}/>Estender trial</button></>}
          {step === 2 && <button className="btn primary" onClick={onClose}><Icon name="check" size={13}/>Concluído</button>}
        </div>
      </div>
    </div>
  );
};

// ── Modal: Enviar E-mail Comercial ───────────────────────────────────────────
const TrialEmailModal = ({ open, trial, onClose, onConfirm }) => {
  const TMPL_DEFS = [
    { id:'reminder', label:'Lembrete de fim de trial',  subject:'Seu trial está acabando — não perca o acesso' },
    { id:'offer',    label:'Oferta de conversão',        subject:'Oferta especial: converta agora com desconto' },
    { id:'schedule', label:'Agendar conversa',           subject:'Que tal uma conversa sobre sua experiência?' },
  ];
  const getBody = (id, t) => {
    const exp = t?.trial_ends_at || 'breve';
    if (id === 'reminder') return `Olá,\n\nSeu período de trial do Tezus está chegando ao fim em ${exp}.\n\nNão perca o acesso às suas funcionalidades. Converta agora para continuar usando a plataforma.`;
    if (id === 'offer')    return `Olá,\n\nSabemos que você está avaliando o Tezus, e queremos ajudá-lo a dar o próximo passo.\n\nEntre em contato conosco para uma oferta especial de conversão.`;
    if (id === 'schedule') return `Olá,\n\nGostaríamos de entender como está sendo sua experiência com o Tezus.\n\nPodemos agendar uma conversa de 20 minutos para tirar dúvidas?`;
    return '';
  };
  const [tmpl,     setTmpl]     = useStateBL('reminder');
  const [subject,  setSubject]  = useStateBL(TMPL_DEFS[0].subject);
  const [body,     setBody]     = useStateBL('');
  const [sendCopy, setSendCopy] = useStateBL(false);
  const [step,     setStep]     = useStateBL(0);
  useEffectBL(() => {
    if (open && trial) { setTmpl('reminder'); setSubject(TMPL_DEFS[0].subject); setBody(getBody('reminder', trial)); setSendCopy(false); setStep(0); }
  }, [open]);
  useEffectBL(() => {
    const def = TMPL_DEFS.find(t => t.id === tmpl);
    if (def && trial) { setSubject(def.subject); setBody(getBody(tmpl, trial)); }
  }, [tmpl]);
  if (!open || !trial) return null;
  const handleSend = () => { setStep(1); setTimeout(() => { setStep(2); onConfirm && onConfirm({ trial, tmpl }); }, 1400); };
  return (
    <div onClick={step > 0 ? undefined : onClose} style={{ position:'fixed',inset:0,background:'rgba(10,22,40,0.44)',backdropFilter:'blur(4px)',zIndex:900,display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width:520,maxHeight:'90vh',background:'var(--bg-elevated)',borderRadius:16,boxShadow:'var(--shadow-lg)',border:'1px solid var(--border)',display:'flex',flexDirection:'column',overflow:'hidden' }}>
        <div style={{ padding:'18px 22px 14px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12,flexShrink:0 }}>
          <div style={{ width:36,height:36,borderRadius:8,background:'var(--info-bg)',display:'flex',alignItems:'center',justifyContent:'center' }}><Icon name="mail" size={16} style={{ color:'var(--info)' }}/></div>
          <div style={{ flex:1 }}><div style={{ fontWeight:700,fontSize:15 }}>Enviar e-mail comercial</div><div style={{ fontSize:12,color:'var(--text-muted)' }}>{trial.tenant_name}</div></div>
          {step === 0 && <button className="icon-action" onClick={onClose}><Icon name="x" size={15}/></button>}
        </div>
        <div style={{ flex:1,overflowY:'auto',padding:'18px 22px' }}>
          {step === 0 && <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
            <div style={{ display:'flex',justifyContent:'space-between',padding:'9px 0',borderBottom:'1px solid var(--border)',fontSize:13 }}>
              <span style={{ color:'var(--text-muted)',fontSize:12 }}>Destinatário</span>
              <span style={{ fontWeight:500 }}>{trial.billing_email}</span>
            </div>
            <div>
              <div style={{ fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:8 }}>Template</div>
              <div style={{ display:'flex',flexDirection:'column',gap:6 }}>
                {TMPL_DEFS.map(t => (
                  <label key={t.id} style={{ display:'flex',alignItems:'center',gap:10,cursor:'pointer',padding:'9px 12px',borderRadius:8,border:`1.5px solid ${tmpl===t.id?'var(--brand-navy)':'var(--border)'}`,background:tmpl===t.id?'color-mix(in srgb,var(--brand-navy) 6%,var(--bg-elevated))':'var(--bg-elevated)',transition:'all 120ms' }}>
                    <input type="radio" checked={tmpl===t.id} onChange={() => setTmpl(t.id)} style={{ accentColor:'var(--brand-navy)',flexShrink:0 }}/>
                    <span style={{ fontSize:13,color:tmpl===t.id?'var(--brand-navy)':'var(--text-secondary)',fontWeight:tmpl===t.id?600:400 }}>{t.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label style={{ display:'block',fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:6 }}>Assunto</label>
              <input value={subject} onChange={e => setSubject(e.target.value)} style={{ width:'100%',height:36,padding:'0 12px',borderRadius:8,border:'1px solid var(--border-strong)',background:'var(--bg-elevated)',color:'var(--text)',fontSize:13,fontFamily:'inherit' }}/>
            </div>
            <div>
              <label style={{ display:'block',fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:6 }}>Mensagem</label>
              <textarea value={body} onChange={e => setBody(e.target.value)} style={{ width:'100%',minHeight:96,padding:'8px 12px',border:'1px solid var(--border-strong)',borderRadius:8,background:'var(--bg-elevated)',color:'var(--text)',fontSize:13,fontFamily:'inherit',resize:'vertical',lineHeight:1.6 }}/>
            </div>
            <label style={{ display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13 }}>
              <input type="checkbox" checked={sendCopy} onChange={e => setSendCopy(e.target.checked)} style={{ accentColor:'var(--brand-navy)',width:14,height:14 }}/>
              <span style={{ color:'var(--text-secondary)' }}>Enviar cópia para o time comercial</span>
            </label>
          </div>}
          {step === 1 && <div style={{ textAlign:'center',padding:'32px 0' }}>
            <div style={{ width:44,height:44,borderRadius:'50%',border:'3px solid var(--border)',borderTop:'3px solid var(--brand-navy)',animation:'spin 0.8s linear infinite',margin:'0 auto 16px' }}/>
            <div style={{ fontWeight:600 }}>Enviando e-mail…</div>
          </div>}
          {step === 2 && <div style={{ textAlign:'center',padding:'32px 0' }}>
            <div style={{ width:52,height:52,borderRadius:'50%',background:'var(--success-bg)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px' }}><Icon name="check" size={22} style={{ color:'var(--success)' }}/></div>
            <div style={{ fontWeight:700,fontSize:15 }}>E-mail enviado!</div>
            <div style={{ fontSize:13,color:'var(--text-muted)',marginTop:6 }}>Para {trial.billing_email} · {TMPL_DEFS.find(t => t.id===tmpl)?.label}</div>
          </div>}
        </div>
        <div style={{ padding:'12px 22px',borderTop:'1px solid var(--border)',background:'var(--bg-subtle)',display:'flex',gap:8,justifyContent:'flex-end',flexShrink:0 }}>
          {step === 0 && <><button className="btn ghost" onClick={onClose}>Cancelar</button><button className="btn primary" onClick={handleSend}><Icon name="mail" size={13}/>Enviar e-mail</button></>}
          {step === 2 && <button className="btn primary" onClick={onClose}><Icon name="check" size={13}/>Concluído</button>}
        </div>
      </div>
    </div>
  );
};

// ── Tab: Trials ──────────────────────────────────────────────────────────────
const TabTrials = ({ subs, onToast }) => {
  const trials = subs.filter(s => s.status === 'trialing');
  const [convertItem, setConvertItem] = useStateBL(null);
  const [extendItem,  setExtendItem]  = useStateBL(null);
  const [emailItem,   setEmailItem]   = useStateBL(null);
  const [localStates, setLocalStates] = useStateBL({});

  const addHistory = (event, tenant, amount = 0) => {
    const ts = 'Hoje, ' + new Date().toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' });
    if (Array.isArray(FINANCIAL_HISTORY_MOCK)) {
      FINANCIAL_HISTORY_MOCK.unshift({ id: 'fh_' + Date.now(), date: ts, tenant, event, amount, actor: 'Admin', origin: 'Super Admin', result: 'success' });
    }
  };

  const daysDiff = (dateStr) => {
    if (!dateStr) return null;
    try {
      const end = new Date(dateStr);
      if (isNaN(end.getTime())) return null;
      return Math.round((end - new Date()) / (1000 * 60 * 60 * 24));
    } catch (e) { return null; }
  };

  return (
    <div>
      <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'var(--bg-subtle)' }}>
            <tr>{['Tenant', 'Plano', 'Início', 'Fim do trial', 'Dias restantes', 'Método cadastrado', 'Status', 'Ações'].map(h => (
              <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {trials.map(s => {
              const local       = localStates[s.id] || {};
              const curExpiry   = local.newExpiry || s.trial_ends_at;
              const days        = daysDiff(curExpiry);
              const isExpiring  = days !== null && days <= 5;
              const isConverted = local.status === 'converted';
              return (
                <tr key={s.id} style={{ borderTop: '1px solid var(--border)', background: isConverted ? 'color-mix(in srgb, var(--success-bg) 25%, transparent)' : isExpiring ? 'color-mix(in srgb, var(--warning-bg) 30%, transparent)' : '' }}>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{s.tenant_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.billing_email}</div>
                  </td>
                  <td style={{ padding: '11px 14px' }}><PlanBadge plan={s.plan_id} /></td>
                  <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{s.current_period_start}</td>
                  <td style={{ padding: '11px 14px', fontSize: 12 }}>
                    <span style={{ color: local.newExpiry ? 'var(--info)' : 'var(--text-secondary)', fontWeight: local.newExpiry ? 600 : 400 }}>{curExpiry || '—'}</span>
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    {days !== null
                      ? <span style={{ fontWeight: 700, fontSize: 13, color: isExpiring ? 'var(--danger)' : 'var(--text)' }}>{days} dias</span>
                      : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    {s.payment_method
                      ? <span style={{ fontSize: 12, color: '#0f7a3e', fontWeight: 600 }}>✓ {s.payment_method}</span>
                      : <span style={{ fontSize: 12, color: 'var(--danger)', fontWeight: 600 }}>✗ Não cadastrado</span>}
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ display:'flex',flexDirection:'column',gap:3 }}>
                      {isConverted && <span className="badge success" style={{ fontSize:10 }}><span className="dot"/>Convertido</span>}
                      {local.extended && !isConverted && <span className="badge info" style={{ fontSize:10 }}><span className="dot"/>Trial estendido</span>}
                      {local.emailSent && <div style={{ fontSize:10,color:'var(--text-muted)',fontStyle:'italic',marginTop:1 }}>E-mail enviado</div>}
                      {!isConverted && !local.extended && !local.emailSent && <span className="badge neutral" style={{ fontSize:10 }}><span className="dot"/>Em trial</span>}
                    </div>
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="icon-action" title="Converter para pago" disabled={isConverted} onClick={() => setConvertItem(s)} style={{ opacity: isConverted ? 0.3 : 1 }}><Icon name="check" size={13} /></button>
                      <button className="icon-action" title="Estender trial" disabled={isConverted} onClick={() => setExtendItem(s)} style={{ opacity: isConverted ? 0.3 : 1 }}><Icon name="plus" size={13} /></button>
                      <button className="icon-action" title="Enviar e-mail comercial" onClick={() => setEmailItem(s)}><Icon name="mail" size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {trials.length === 0 && <EmptyState icon="clock" title="Nenhum trial ativo" />}
      </div>

      <ConvertTrialModal
        open={!!convertItem} trial={convertItem} onClose={() => setConvertItem(null)}
        onConfirm={({ trial, plan, cycle }) => {
          setLocalStates(p => ({ ...p, [trial.id]: { ...p[trial.id], status: 'converted' } }));
          addHistory(`Trial convertido — plano ${plan.label} (${cycle === 'monthly' ? 'mensal' : 'anual'})`, trial.tenant_name, plan[cycle]);
          onToast && onToast(`${trial.tenant_name} convertido para o plano ${plan.label}`);
        }}
      />
      <ExtendTrialModal
        open={!!extendItem} trial={extendItem} onClose={() => setExtendItem(null)}
        onConfirm={({ trial, days, reason }) => {
          try {
            const base = trial.trial_ends_at ? new Date(trial.trial_ends_at) : new Date();
            base.setDate(base.getDate() + days);
            const newExpiry = base.toISOString().split('T')[0];
            setLocalStates(p => ({ ...p, [trial.id]: { ...p[trial.id], newExpiry, extended: true } }));
          } catch (e) {
            setLocalStates(p => ({ ...p, [trial.id]: { ...p[trial.id], extended: true } }));
          }
          addHistory(`Trial estendido +${days} dias${reason ? ' · ' + reason.slice(0, 40) : ''}`, trial.tenant_name);
          onToast && onToast(`Trial de ${trial.tenant_name} estendido +${days} dias`);
        }}
      />
      <TrialEmailModal
        open={!!emailItem} trial={emailItem} onClose={() => setEmailItem(null)}
        onConfirm={({ trial, tmpl }) => {
          setLocalStates(p => ({ ...p, [trial.id]: { ...p[trial.id], emailSent: true } }));
          addHistory(`E-mail comercial enviado (${tmpl})`, trial.tenant_name);
          onToast && onToast(`E-mail comercial enviado para ${trial.tenant_name}`);
        }}
      />
    </div>
  );
};

// ── Tab: Régua de cobrança ──────────────────────────────────────────────────
const TabRegua = ({ onToast }) => {
  const [rules, setRules] = useStateBL(DUNNING_RULES_MOCK || []);
  const [reguaSaved,     setReguaSaved]     = useStateBL(false);
  const [reguaLastSaved, setReguaLastSaved] = useStateBL(null);
  const [reguaSaving,    setReguaSaving]    = useStateBL(false);

  const toggle = (id) => {
    setRules(rs => rs.map(r => r.id === id ? { ...r, enabled: !r.enabled } : r));
    setReguaSaved(false);
  };

  const handleSaveRegua = () => {
    setReguaSaving(true);
    setTimeout(() => {
      const now  = new Date();
      const ts   = 'Hoje, ' + now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      setReguaSaving(false);
      setReguaSaved(true);
      setReguaLastSaved(ts);
      // Registra no histórico financeiro mockado
      if (Array.isArray(FINANCIAL_HISTORY_MOCK)) {
        FINANCIAL_HISTORY_MOCK.unshift({
          id: 'fh_' + Date.now(),
          date: ts,
          tenant: 'Sistema',
          event: 'Régua de cobrança atualizada',
          amount: 0,
          actor: 'Ana Vargas',
          origin: 'Super Admin',
          result: 'success',
        });
      }
      onToast && onToast('Régua de cobrança salva com sucesso');
    }, 700);
  };

  const actionLabels = { send_email: 'Enviar e-mail', send_reminder: 'Lembrete', mark_overdue: 'Marcar inadimplente', partial_suspend: 'Suspensão parcial', full_suspend: 'Suspensão total', cancel: 'Cancelar tenant' };
  const actionColors = { send_email: '#1d4ed8', send_reminder: '#1d4ed8', mark_overdue: '#b45309', partial_suspend: '#EC2A58', full_suspend: '#EC2A58', cancel: '#EC2A58' };

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Ações automáticas disparadas a partir do dia de vencimento da fatura.</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {reguaLastSaved && (
            <span style={{ fontSize: 12, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Icon name="check" size={12} />Última atualização: {reguaLastSaved}
            </span>
          )}
          <button
            className="btn primary"
            onClick={handleSaveRegua}
            disabled={reguaSaving}
            style={{ minWidth: 128, opacity: reguaSaving ? 0.75 : 1, transition: 'background 200ms' }}
          >
            {reguaSaving
              ? <><div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', animation: 'spin 0.8s linear infinite' }} />Salvando…</>
              : reguaSaved
                ? <><Icon name="check" size={13} />Salvo ✓</>
                : <><Icon name="check" size={13} />Salvar régua</>
            }
          </button>
        </div>
      </div>
      {reguaSaved && reguaLastSaved && (
        <div style={{ marginBottom: 14, padding: '10px 14px', background: 'var(--success-bg)', border: '1px solid color-mix(in srgb, var(--success) 25%, transparent)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--success)' }}>
          <Icon name="check" size={14} />
          <span><strong>Régua salva.</strong> As regras serão aplicadas automaticamente a partir do próximo ciclo de cobrança.</span>
        </div>
      )}
      <div style={{ position: 'relative', paddingLeft: 24 }}>
        {rules.map((r, idx) => {
          const isSystem = r.action === 'mark_overdue' || r.action === 'partial_suspend' || r.action === 'full_suspend' || r.action === 'cancel';
          const color = actionColors[r.action] || '#1d4ed8';
          return (
            <div key={r.id} style={{ display: 'flex', gap: 0, position: 'relative', marginBottom: idx < rules.length - 1 ? 0 : 0 }}>
              {idx < rules.length - 1 && <div style={{ position: 'absolute', left: -13, top: 28, bottom: 0, width: 2, background: 'var(--border)' }} />}
              <div style={{ position: 'absolute', left: -18, top: 8, width: 10, height: 10, borderRadius: '50%', background: r.enabled ? color : 'var(--border-strong)', border: '2px solid var(--bg-elevated)' }} />
              <div style={{ flex: 1, marginBottom: 12, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', opacity: r.enabled ? 1 : 0.5, transition: 'opacity 150ms' }}>
                <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ minWidth: 44, textAlign: 'center', padding: '2px 8px', borderRadius: 20, background: 'var(--bg-subtle)', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>
                    D+{r.days_after_due}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Canal: {r.channel}</div>
                  </div>
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 20, background: color + '18', color, fontSize: 11, fontWeight: 700 }}>
                    {actionLabels[r.action]}
                  </span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>
                    <input type="checkbox" checked={r.enabled} onChange={() => toggle(r.id)} style={{ width: 14, height: 14, accentColor: 'var(--brand-navy)' }} />
                    Ativo
                  </label>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Tab: Histórico financeiro ────────────────────────────────────────────────
const TabHistoricoFinanceiro = () => {
  const history = FINANCIAL_HISTORY_MOCK || [];
  const resultConfig = { success: { label: 'Sucesso', color: '#0f7a3e' }, error: { label: 'Erro', color: '#EC2A58' }, pending: { label: 'Pendente', color: '#b45309' } };

  return (
    <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ background: 'var(--bg-subtle)' }}>
          <tr>{['Data/hora', 'Tenant', 'Evento', 'Valor', 'Ator', 'Origem', 'Resultado'].map(h => (
            <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {history.map(h => {
            const res = resultConfig[h.result] || resultConfig.pending;
            return (
              <tr key={h.id} style={{ borderTop: '1px solid var(--border)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h.date}</td>
                <td style={{ padding: '10px 14px', fontWeight: 500, fontSize: 13 }}>{h.tenant}</td>
                <td style={{ padding: '10px 14px', fontSize: 13 }}>{h.event}</td>
                <td style={{ padding: '10px 14px', fontWeight: 600, fontSize: 13 }}>{h.amount > 0 ? fmtBRL(h.amount) : '—'}</td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)' }}>{h.actor}</td>
                <td style={{ padding: '10px 14px', fontSize: 11, color: 'var(--text-muted)' }}>{h.origin}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ fontWeight: 600, fontSize: 12, color: res.color }}>{res.label}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Modal: Exportar Faturas ──────────────────────────────────────────────────
const ExportarFaturasModal = ({ open, onClose, onConfirm }) => {
  const [form, setForm] = useStateBL({ period:'30d', status:'todas', format:'CSV', includeData:true, includeHistory:false, includeOverdue:false });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  if (!open) return null;
  const PERIODS = [{v:'7d',l:'Últimos 7 dias'},{v:'30d',l:'Últimos 30 dias'},{v:'current',l:'Mês atual'},{v:'prev',l:'Mês anterior'},{v:'custom',l:'Personalizado'}];
  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(10,22,40,0.44)',backdropFilter:'blur(3px)',zIndex:900,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{width:480,background:'var(--bg-elevated)',borderRadius:16,boxShadow:'var(--shadow-lg)',border:'1px solid var(--border)',overflow:'hidden'}}>
        <div style={{padding:'18px 22px 14px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12}}>
          <div style={{width:36,height:36,borderRadius:8,background:'var(--info-bg)',display:'flex',alignItems:'center',justifyContent:'center'}}><Icon name="download" size={16} style={{color:'var(--info)'}}/></div>
          <div><div style={{fontWeight:600,fontSize:15}}>Exportar faturas</div><div style={{fontSize:12,color:'var(--text-muted)'}}>Selecione período, status e formato</div></div>
          <button className="icon-action" style={{marginLeft:'auto'}} onClick={onClose}><Icon name="x" size={15}/></button>
        </div>
        <div style={{padding:'20px 22px',display:'flex',flexDirection:'column',gap:16}}>
          <div>
            <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:8}}>Período</label>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {PERIODS.map(opt=>(
                <button key={opt.v} onClick={()=>set('period',opt.v)} style={{padding:'5px 12px',borderRadius:8,border:`1.5px solid ${form.period===opt.v?'var(--brand-navy)':'var(--border-strong)'}`,background:form.period===opt.v?'color-mix(in srgb,var(--brand-navy) 8%,var(--bg-elevated))':'var(--bg-elevated)',color:form.period===opt.v?'var(--brand-navy)':'var(--text-secondary)',fontSize:12,fontWeight:form.period===opt.v?600:400,cursor:'pointer'}}>{opt.l}</button>
              ))}
            </div>
            {form.period==='custom'&&(
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginTop:10}}>
                {['De','Até'].map(f=>(
                  <div key={f}><label style={{display:'block',fontSize:11,color:'var(--text-muted)',marginBottom:4}}>{f}</label><input type="date" style={{width:'100%',height:34,padding:'0 10px',border:'1px solid var(--border-strong)',borderRadius:8,background:'var(--bg-elevated)',color:'var(--text)',fontSize:13,fontFamily:'inherit'}}/></div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:6}}>Status da fatura</label>
            <select className="select" style={{width:'100%',height:34}} value={form.status} onChange={e=>set('status',e.target.value)}>
              {[['todas','Todas'],['pagas','Pagas'],['pendentes','Pendentes'],['vencidas','Vencidas'],['canceladas','Canceladas']].map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:6}}>Formato</label>
            <div style={{display:'flex',gap:8}}>
              {['CSV','XLSX','PDF'].map(fmt=>(
                <button key={fmt} onClick={()=>set('format',fmt)} style={{flex:1,padding:'8px',borderRadius:8,border:`1.5px solid ${form.format===fmt?'var(--brand-navy)':'var(--border-strong)'}`,background:form.format===fmt?'color-mix(in srgb,var(--brand-navy) 8%,var(--bg-elevated))':'var(--bg-elevated)',color:form.format===fmt?'var(--brand-navy)':'var(--text-secondary)',fontSize:13,fontWeight:form.format===fmt?600:500,cursor:'pointer',textAlign:'center'}}>{fmt}</button>
              ))}
            </div>
          </div>
          <div>
            <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:8}}>Incluir nos dados</label>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {[{k:'includeData',l:'Dados do tenant (nome, CNPJ, e-mail)'},{k:'includeHistory',l:'Histórico de cobrança'},{k:'includeOverdue',l:'Inadimplência e tentativas'}].map(opt=>(
                <label key={opt.k} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13}}>
                  <input type="checkbox" checked={form[opt.k]} onChange={e=>set(opt.k,e.target.checked)} style={{width:14,height:14,accentColor:'var(--brand-navy)'}}/>
                  <span style={{color:'var(--text-secondary)'}}>{opt.l}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div style={{padding:'12px 22px',borderTop:'1px solid var(--border)',background:'var(--bg-subtle)',display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={()=>onConfirm(form)}><Icon name="download" size={13}/>Exportar</button>
        </div>
      </div>
    </div>
  );
};

// ── Modal: Nova Cobrança Manual ──────────────────────────────────────────────
const NovaCobracaModal = ({ open, onClose, onToast, onAdd }) => {
  const [submitted, setSubmitted] = useStateBL(false);
  const [form, setForm] = useStateBL({tenant:'',plan:'',description:'',amount:'',dueDate:'',method:'Pix',status:'open',sendEmail:true,notes:''});
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const tenants = SUBSCRIPTIONS_MOCK || [];
  const required = ['tenant','description','amount','dueDate'];
  const isValid = required.every(k=>form[k].toString().trim());

  useEffectBL(() => {
    if (!open) { setSubmitted(false); setForm({tenant:'',plan:'',description:'',amount:'',dueDate:'',method:'Pix',status:'open',sendEmail:true,notes:''}); }
  }, [open]);

  if (!open) return null;

  const inputSt = (k) => ({width:'100%',height:34,padding:'0 10px',border:`1px solid ${submitted&&!form[k]?.toString().trim()?'var(--danger)':'var(--border-strong)'}`,borderRadius:'var(--radius)',background:'var(--bg-elevated)',color:'var(--text)',fontSize:13,fontFamily:'inherit'});
  const Lbl = ({t,req}) => <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:5}}>{t}{req&&<span style={{color:'var(--danger)'}}> *</span>}</label>;
  const Err = ({k}) => submitted&&!form[k]?.toString().trim() ? <div style={{fontSize:11,color:'var(--danger)',marginTop:3}}>Obrigatório</div> : null;

  const handleCreate = (withEmail) => {
    setSubmitted(true);
    if (!isValid) return;
    const newInv = {
      id:'inv_'+Date.now(), number:'INV-2026-'+(1100+Math.floor(Math.random()*100)),
      tenant_id:99, tenant_name:form.tenant, amount:parseFloat(form.amount)||0,
      status:form.status, due_date:form.dueDate, paid_at:form.status==='paid'?'Agora':null,
      method:form.method, attempts:0,
    };
    if (onAdd) onAdd(newInv);
    onToast&&onToast(`Cobrança criada${withEmail?' — e-mail enviado ao cliente':''}`);
    onClose();
  };

  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(10,22,40,0.44)',backdropFilter:'blur(3px)',zIndex:900,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{width:560,maxHeight:'90vh',background:'var(--bg-elevated)',borderRadius:16,boxShadow:'var(--shadow-lg)',border:'1px solid var(--border)',display:'flex',flexDirection:'column'}}>
        <div style={{padding:'18px 22px 14px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12}}>
          <div style={{width:36,height:36,borderRadius:8,background:'var(--success-bg)',display:'flex',alignItems:'center',justifyContent:'center'}}><Icon name="plus" size={16} style={{color:'var(--success)'}}/></div>
          <div><div style={{fontWeight:600,fontSize:15}}>Nova cobrança manual</div><div style={{fontSize:12,color:'var(--text-muted)'}}>Criar fatura fora do ciclo de assinatura</div></div>
          <button className="icon-action" style={{marginLeft:'auto'}} onClick={onClose}><Icon name="x" size={15}/></button>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'20px 22px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
            <div>
              <Lbl t="Tenant / Loja" req/>
              <select className="select" style={{width:'100%',height:34,border:`1px solid ${submitted&&!form.tenant?'var(--danger)':'var(--border-strong)'}`}} value={form.tenant} onChange={e=>set('tenant',e.target.value)}>
                <option value="">Selecionar…</option>
                {tenants.map(t=><option key={t.id} value={t.tenant_name}>{t.tenant_name}</option>)}
              </select>
              <Err k="tenant"/>
            </div>
            <div>
              <Lbl t="Assinatura relacionada"/>
              <select className="select" style={{width:'100%',height:34}} value={form.plan} onChange={e=>set('plan',e.target.value)}>
                <option value="">Nenhuma</option>
                {tenants.filter(t=>!form.tenant||t.tenant_name===form.tenant).map(t=><option key={t.id} value={t.plan}>{t.plan}</option>)}
              </select>
            </div>
          </div>
          <div style={{marginBottom:14}}><Lbl t="Descrição" req/><input style={inputSt('description')} value={form.description} onChange={e=>set('description',e.target.value)} placeholder="Ex.: Serviço de migração de dados"/><Err k="description"/></div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
            <div><Lbl t="Valor (R$)" req/><input style={inputSt('amount')} type="number" min="0" step="0.01" value={form.amount} onChange={e=>set('amount',e.target.value)} placeholder="0,00"/><Err k="amount"/></div>
            <div><Lbl t="Vencimento" req/><input type="date" style={inputSt('dueDate')} value={form.dueDate} onChange={e=>set('dueDate',e.target.value)}/><Err k="dueDate"/></div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
            <div>
              <Lbl t="Método"/>
              <select className="select" style={{width:'100%',height:34}} value={form.method} onChange={e=>set('method',e.target.value)}>
                {['Pix','Boleto','Cartão','Manual'].map(m=><option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <Lbl t="Status inicial"/>
              <select className="select" style={{width:'100%',height:34}} value={form.status} onChange={e=>set('status',e.target.value)}>
                <option value="open">Pendente</option>
                <option value="paid">Paga</option>
                <option value="scheduled">Agendada</option>
              </select>
            </div>
          </div>
          <div style={{marginBottom:14}}>
            <Lbl t="Observação interna"/>
            <textarea value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Visível apenas para a equipe" style={{width:'100%',minHeight:68,padding:'8px 10px',border:'1px solid var(--border-strong)',borderRadius:'var(--radius)',background:'var(--bg-elevated)',color:'var(--text)',fontSize:13,fontFamily:'inherit',resize:'vertical'}}/>
          </div>
          <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13}}>
            <input type="checkbox" checked={form.sendEmail} onChange={e=>set('sendEmail',e.target.checked)} style={{width:14,height:14,accentColor:'var(--brand-navy)'}}/>
            <span style={{color:'var(--text-secondary)'}}>Enviar e-mail de cobrança ao cliente</span>
          </label>
        </div>
        <div style={{padding:'12px 22px',borderTop:'1px solid var(--border)',background:'var(--bg-subtle)',display:'flex',gap:8,justifyContent:'flex-end',alignItems:'center'}}>
          <span style={{fontSize:12,color:'var(--text-muted)',marginRight:'auto'}}>* Obrigatórios</span>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn" onClick={()=>handleCreate(false)}><Icon name="plus" size={13}/>Criar cobrança</button>
          <button className="btn primary" onClick={()=>handleCreate(true)}><Icon name="mail" size={13}/>Criar e enviar e-mail</button>
        </div>
      </div>
    </div>
  );
};

// ── Tab: Status comercial (F6 — consolidado) ─────────────────────────────────
const TabStatusComercial = ({ currentUser, onToast, onNav, onBillingChanged }) => {
  const canManage = typeof saBillingCanManage === 'function'
    ? saBillingCanManage(currentUser)
    : false;

  const [rows, setRows] = useStateBL([]);
  const [loading, setLoading] = useStateBL(true);
  const [error, setError] = useStateBL(null);
  const [searchInput, setSearchInput] = useStateBL('');
  const [query, setQuery] = useStateBL('');
  const [statusFilter, setStatusFilter] = useStateBL('');
  const [planFilter, setPlanFilter] = useStateBL('');
  const [page, setPage] = useStateBL(1);
  const [total, setTotal] = useStateBL(0);
  const [actionRow, setActionRow] = useStateBL(null);
  const [historyRow, setHistoryRow] = useStateBL(null);
  const [history, setHistory] = useStateBL([]);
  const [historyLoading, setHistoryLoading] = useStateBL(false);
  const [historyError, setHistoryError] = useStateBL(null);
  const pageSize = 20;
  const listRequestRef = useRefBL(0);
  const historyRequestRef = useRefBL(0);
  const historyTriggerRef = useRefBL(null);
  const historyDrawerRef = useRefBL(null);
  const historyCloseRef = useRefBL(null);
  const actionTriggerRef = useRefBL(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const REFRESH_PARTIAL_WARNING = 'Operação concluída, mas não foi possível atualizar todos os dados da tela.';

  const FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function getFocusableElements(container) {
    if (!container) return [];
    const nodes = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));
    return nodes.filter((el) => {
      if (!el || el.disabled) return false;
      const style = window.getComputedStyle(el);
      if (style.visibility === 'hidden' || style.display === 'none') return false;
      if (el.offsetParent === null && style.position !== 'fixed') return false;
      return true;
    });
  }

  function trapFocus(event, container) {
    if (!container || event.key !== 'Tab') return;
    const focusable = getFocusableElements(container);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  useEffectBL(() => {
    const timer = setTimeout(() => {
      setQuery(searchInput.trim().slice(0, 200));
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = React.useCallback(async () => {
    const requestId = ++listRequestRef.current;
    setLoading(true);
    setError(null);
    const params = { page, page_size: pageSize };
    if (query) params.search = query;
    if (statusFilter) params.status = statusFilter;
    if (planFilter) params.plan = planFilter;

    try {
      const response = await billingService.listTenants(params);
      if (requestId !== listRequestRef.current) {
        return { ok: false, stale: true };
      }
      setRows(response.results || []);
      setTotal(response.count || 0);
      return {
        ok: true,
        stale: false,
        data: response,
      };
    } catch (err) {
      if (requestId !== listRequestRef.current) {
        return { ok: false, stale: true };
      }
      const message = typeof formatBillingStatusError !== 'undefined'
        ? formatBillingStatusError(err)
        : (err && err.message) || 'Não foi possível carregar o Status comercial.';
      setError(message);
      return {
        ok: false,
        stale: false,
        error: err,
        message,
      };
    } finally {
      if (requestId === listRequestRef.current) setLoading(false);
    }
  }, [page, query, statusFilter, planFilter]);

  useEffectBL(() => {
    void load();
  }, [load]);

  useEffectBL(() => {
    if (!historyRow) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') {
        closeHistory();
        return;
      }
      trapFocus(event, historyDrawerRef.current);
    };
    window.addEventListener('keydown', onKey);
    const timer = setTimeout(() => {
      if (historyCloseRef.current && typeof historyCloseRef.current.focus === 'function') {
        historyCloseRef.current.focus();
      }
    }, 0);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [historyRow]);

  const openHistory = (row, event) => {
    if (event && event.currentTarget) historyTriggerRef.current = event.currentTarget;
    const requestId = ++historyRequestRef.current;
    setHistoryRow(row);
    setHistory([]);
    setHistoryError(null);
    setHistoryLoading(true);
    billingService.getTenantStatusHistory(row.tenant_id)
      .then((response) => {
        if (requestId !== historyRequestRef.current) return;
        setHistory(response.results || []);
      })
      .catch((err) => {
        if (requestId !== historyRequestRef.current) return;
        setHistoryError(typeof formatBillingStatusError !== 'undefined' ? formatBillingStatusError(err) : err.message);
      })
      .finally(() => {
        if (requestId === historyRequestRef.current) setHistoryLoading(false);
      });
  };

  const closeHistory = () => {
    historyRequestRef.current += 1;
    setHistoryRow(null);
    setHistory([]);
    setHistoryError(null);
    setHistoryLoading(false);
    if (historyTriggerRef.current && typeof historyTriggerRef.current.focus === 'function') {
      historyTriggerRef.current.focus();
    }
  };

  const openAction = (row, event) => {
    if (!canManage) return;
    if (event && event.currentTarget) actionTriggerRef.current = event.currentTarget;
    setActionRow(row);
  };

  const closeAction = () => {
    setActionRow(null);
    if (actionTriggerRef.current && typeof actionTriggerRef.current.focus === 'function') {
      actionTriggerRef.current.focus();
    }
  };

  const handleStatusChange = async ({ status, reason, notes }) => {
    if (!actionRow || !canManage) return;
    const row = actionRow;
    try {
      await billingService.updateTenantBillingStatus(row.tenant_id, { status, reason, notes });
      setActionRow(null);
      let refreshWarning = null;
      try {
        const listRefresh = await load();
        if (
          listRefresh
          && listRefresh.ok === false
          && listRefresh.stale !== true
        ) {
          refreshWarning = REFRESH_PARTIAL_WARNING;
        }
      } catch (_err) {
        refreshWarning = REFRESH_PARTIAL_WARNING;
      }
      if (typeof onBillingChanged === 'function') {
        try {
          const overviewRefresh = await Promise.resolve(
            onBillingChanged(),
          );
          if (
            overviewRefresh
            && overviewRefresh.ok === false
            && overviewRefresh.stale !== true
          ) {
            refreshWarning = REFRESH_PARTIAL_WARNING;
          }
        } catch (_err) {
          if (!refreshWarning) refreshWarning = REFRESH_PARTIAL_WARNING;
        }
      }
      if (refreshWarning && onToast) onToast(refreshWarning);
      else onToast && onToast(`Status de ${row.name} atualizado.`);
      if (actionTriggerRef.current && typeof actionTriggerRef.current.focus === 'function') {
        actionTriggerRef.current.focus();
      }
    } catch (err) {
      onToast && onToast(typeof formatBillingStatusError !== 'undefined' ? formatBillingStatusError(err) : err.message);
    }
  };

  const fmtDt = (value) => {
    if (!value) return '—';
    try { return new Date(value).toLocaleDateString('pt-BR'); } catch { return '—'; }
  };

  const isInitialLoading = loading && rows.length === 0 && !error;
  const showStaleError = Boolean(error && rows.length > 0);

  return (
    <div className="billing-commercial">
      <div className="billing-commercial-toolbar">
        <div className="billing-commercial-search">
          <label htmlFor="billing-commercial-search-input" className="billing-sr-only">
            Buscar tenant comercial
          </label>
          <input
            id="billing-commercial-search-input"
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value.slice(0, 200))}
            placeholder="Buscar tenant..."
            maxLength={200}
            aria-busy={loading || undefined}
          />
          <div className="billing-commercial-search-status" aria-live="polite" aria-atomic="true">
            {loading ? 'Carregando…' : `${total} tenant${total === 1 ? '' : 's'}`}
          </div>
        </div>
        <div className="billing-commercial-filters">
          <label htmlFor="billing-commercial-status-filter">
            <span className="billing-sr-only">Filtrar por status</span>
            <select
              id="billing-commercial-status-filter"
              className="select"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">Todos os status</option>
              <option value="trial">Trial</option>
              <option value="active">Ativo</option>
              <option value="past_due">Inadimplente</option>
              <option value="suspended">Suspenso</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </label>
          <label htmlFor="billing-commercial-plan-filter">
            <span className="billing-sr-only">Filtrar por plano</span>
            <select
              id="billing-commercial-plan-filter"
              className="select"
              value={planFilter}
              onChange={(e) => { setPlanFilter(e.target.value); setPage(1); }}
            >
              <option value="">Todos os planos</option>
              <option value="basic">Basic</option>
              <option value="pro">Pro</option>
            </select>
          </label>
          <button type="button" className="btn ghost" onClick={load} disabled={loading} aria-busy={loading || undefined}>
            <Icon name="refresh" size={13} />Atualizar
          </button>
        </div>
      </div>

      {isInitialLoading && (
        <div className="billing-commercial-skeleton" role="status" aria-label="Carregando status comercial">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="billing-commercial-skel-row" />
          ))}
        </div>
      )}

      {!isInitialLoading && error && rows.length === 0 && (
        typeof ApiErrorState !== 'undefined'
          ? <ApiErrorState error={error} onRetry={load} />
          : <div className="billing-commercial-alert" role="alert">{error}</div>
      )}

      {!isInitialLoading && (!error || rows.length > 0) && (
        <>
          {showStaleError && (
            <div className="billing-commercial-alert" role="alert">
              <span>Não foi possível atualizar o Status comercial. Os dados abaixo podem estar desatualizados.</span>
              <button type="button" className="btn" onClick={load} disabled={loading}>
                Tentar novamente
              </button>
            </div>
          )}
          <div className="billing-commercial-table-wrap">
            <table className="billing-commercial-table">
              <caption className="billing-sr-only">Lista de tenants com status comercial</caption>
              <thead>
                <tr>{['Tenant', 'Plano', 'Status', 'Trial termina', 'Período até', 'Inadimplente desde', 'Atualizado', 'Ações'].map((h) => (
                  <th key={h}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.tenant_id}>
                    <td>{row.name}</td>
                    <td>{typeof PlanBadge !== 'undefined' ? <PlanBadge plan={row.plan} /> : row.plan}</td>
                    <td><BillingStatusBadge status={row.commercial_status} label={row.commercial_status_label} /></td>
                    <td>{fmtDt(row.trial_ends_at)}</td>
                    <td>{fmtDt(row.current_period_ends_at)}</td>
                    <td>{fmtDt(row.past_due_since)}</td>
                    <td>{fmtDt(row.updated_at)}</td>
                    <td>
                      <div className="billing-commercial-actions">
                        {canManage && (
                          <button
                            type="button"
                            className="icon-action"
                            title={`Alterar status comercial de ${row.name}`}
                            aria-label={`Alterar status comercial de ${row.name}`}
                            onClick={(e) => openAction(row, e)}
                          >
                            <Icon name="edit" size={13} />
                          </button>
                        )}
                        <button
                          type="button"
                          className="icon-action"
                          title={`Abrir histórico comercial de ${row.name}`}
                          aria-label={`Abrir histórico comercial de ${row.name}`}
                          onClick={(e) => openHistory(row, e)}
                        >
                          <Icon name="audit" size={13} />
                        </button>
                        {onNav && (
                          <button
                            type="button"
                            className="icon-action"
                            title={`Abrir detalhes do tenant ${row.name}`}
                            aria-label={`Abrir detalhes do tenant ${row.name}`}
                            onClick={() => onNav({ page: 'loja', store: { tenant_id: row.tenant_id, name: row.name } })}
                          >
                            <Icon name="externalLink" size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && typeof EmptyState !== 'undefined' && (
              <EmptyState icon="search" title="Nenhum tenant" description="Ajuste os filtros ou cadastre uma loja." />
            )}
          </div>

          <div className="billing-commercial-mobile-list">
            {rows.map((row) => (
              <div key={`m-${row.tenant_id}`} className="billing-commercial-mobile-card">
                <div className="billing-commercial-mobile-top">
                  <strong>{row.name}</strong>
                  <BillingStatusBadge status={row.commercial_status} label={row.commercial_status_label} />
                </div>
                <div>{typeof PlanBadge !== 'undefined' ? <PlanBadge plan={row.plan} /> : row.plan}</div>
                <div className="billing-commercial-mobile-meta">
                  Trial: {fmtDt(row.trial_ends_at)} · Atualizado: {fmtDt(row.updated_at)}
                </div>
                <div className="billing-commercial-actions">
                  {canManage && (
                    <button
                      type="button"
                      className="btn"
                      onClick={(e) => openAction(row, e)}
                      aria-label={`Alterar status comercial de ${row.name}`}
                    >
                      Alterar status
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn"
                    onClick={(e) => openHistory(row, e)}
                    aria-label={`Abrir histórico comercial de ${row.name}`}
                  >
                    Histórico
                  </button>
                </div>
              </div>
            ))}
          </div>

          <nav className="billing-commercial-pagination" aria-label="Paginação de status comercial">
            <div aria-live="polite" aria-atomic="true">
              {total} registro{total === 1 ? '' : 's'} · Página {page} de {totalPages}
            </div>
            <div className="billing-commercial-pagination-controls">
              <button
                type="button"
                className="btn"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <button
                type="button"
                className="btn"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
              </button>
            </div>
          </nav>
        </>
      )}

      {typeof CommercialStatusReasonModal !== 'undefined' && (
        <CommercialStatusReasonModal
          open={!!actionRow}
          tenantName={actionRow?.name}
          currentStatus={actionRow?.commercial_status}
          onClose={closeAction}
          onConfirm={handleStatusChange}
        />
      )}

      {historyRow && (
        <div
          className="billing-commercial-history-backdrop"
          onClick={closeHistory}
        >
          <div
            ref={historyDrawerRef}
            className="billing-commercial-history-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="billing-commercial-history-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="billing-commercial-history-header">
              <h2 id="billing-commercial-history-title">Histórico — {historyRow.name}</h2>
              <button
                type="button"
                className="icon-action"
                ref={historyCloseRef}
                onClick={closeHistory}
                aria-label={`Fechar histórico comercial de ${historyRow.name}`}
              >
                <Icon name="x" size={16} />
              </button>
            </div>
            <div className="billing-commercial-history-body">
              {historyLoading && (
                <div aria-busy="true" aria-live="polite">Carregando histórico…</div>
              )}
              {historyError && (
                <div className="billing-commercial-alert" role="alert">
                  <span>{historyError}</span>
                  <button type="button" className="btn" onClick={() => openHistory(historyRow)}>
                    Tentar novamente
                  </button>
                </div>
              )}
              {!historyLoading && !historyError && history.length === 0 && (
                <div className="billing-commercial-empty">Nenhum evento registrado.</div>
              )}
              {!historyLoading && !historyError && typeof TenantStatusTimeline !== 'undefined' && history.length > 0 && (
                <TenantStatusTimeline history={history} currentStatus={historyRow.commercial_status} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Shell de Billing — abas reais e mock ─────────────────────────────────────
const BILLING_REAL_TABS = [
  { id: 'overview', label: 'Visão geral' },
  { id: 'assinaturas', label: 'Assinaturas' },
  { id: 'faturas', label: 'Faturas' },
  { id: 'comercial', label: 'Status comercial' },
];

const BILLING_MOCK_TABS = [
  { id: 'overview', label: 'Visão geral' },
  { id: 'assinaturas', label: 'Assinaturas' },
  { id: 'faturas', label: 'Faturas' },
  { id: 'comercial', label: 'Status comercial' },
  {
    id: 'inadimplencia',
    label: 'Inadimplência',
    prototype: true,
    mockLabel: 'Dados simulados',
  },
  {
    id: 'trials',
    label: 'Trials',
    prototype: true,
    mockLabel: 'Dados simulados',
  },
  {
    id: 'regua',
    label: 'Régua de cobrança',
    prototype: true,
    mockLabel: 'Dados simulados',
  },
  {
    id: 'historico',
    label: 'Histórico financeiro',
    prototype: true,
    mockLabel: 'Dados simulados',
  },
];

const BillingMissingComponent = ({ title, scriptPath }) => (
  <div className="billing-page-missing hint-card" style={{ padding: 24, textAlign: 'center' }}>
    <div style={{ fontWeight: 600, marginBottom: 8 }}>{title}</div>
    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
      O script <code>{scriptPath}</code> não está disponível. Verifique a ordem de carregamento em <code>index.html</code>.
    </div>
  </div>
);

// ── Página principal de Billing ──────────────────────────────────────────────
const BillingPage = ({ onToast, onNav, stores, tenantContext }) => {
  const useRealBilling = typeof saIsRealBillingMode === 'function'
    ? saIsRealBillingMode()
    : (
      typeof USE_REAL_BILLING_API !== 'undefined'
      && USE_REAL_BILLING_API
    );

  const [activeTab, setActiveTab] = useStateBL('overview');
  const tabButtonRefs = useRefBL({});
  const { data: overview, loading: ovLoading, error: ovError, refetch: refetchOverview } = useBillingOverview();
  const [subs]  = useStateBL(SUBSCRIPTIONS_MOCK || []);
  const [invs, setInvs] = useStateBL(INVOICES_MOCK || []);
  const [showExport,       setShowExport]       = useStateBL(false);
  const [showNovaCobranca, setShowNovaCobranca] = useStateBL(false);
  const [ultimaExportacao, setUltimaExportacao] = useStateBL(null);

  const authState = typeof readAuthState === 'function'
    ? readAuthState()
    : null;
  const currentUser = authState?.user || null;

  const visibleTabs = useRealBilling ? BILLING_REAL_TABS : BILLING_MOCK_TABS;

  const selectBillingTab = (tabId) => {
    const allowed = visibleTabs.some((tab) => tab.id === tabId);
    if (!allowed) {
      setActiveTab('overview');
      return;
    }
    setActiveTab(tabId);
  };

  useEffectBL(() => {
    const remainsAvailable = visibleTabs.some((tab) => tab.id === activeTab);
    if (!remainsAvailable) {
      setActiveTab('overview');
    }
  }, [useRealBilling, activeTab]);

  useEffectBL(() => {
    const activeTabButton = tabButtonRefs.current[activeTab];
    if (activeTabButton && typeof activeTabButton.scrollIntoView === 'function') {
      activeTabButton.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'auto',
      });
    }
  }, [activeTab]);

  const MOCK_KPIS = [
    { label: 'MRR', value: fmtK(overview?.mrr || 15420), sub: 'Mock de desenvolvimento', color: 'var(--brand-navy)' },
    { label: 'ARR', value: fmtK(overview?.arr || 185040), sub: 'Mock de desenvolvimento', color: 'var(--brand-navy)' },
    { label: 'Receita vencida', value: fmtK(overview?.overdue_revenue || 1196), sub: 'Mock de desenvolvimento', color: 'var(--danger)' },
    { label: 'Trials ativos', value: overview?.active_trials || 8, sub: 'Mock de desenvolvimento', color: 'var(--info)' },
    { label: 'Churn mensal', value: fmtPct(overview?.monthly_churn || 0.02), sub: 'Mock de desenvolvimento', color: 'var(--warning)' },
    { label: 'Recebido no mês', value: fmtK(overview?.payments_received_month || 12850), sub: 'Mock de desenvolvimento', color: 'var(--success)' },
  ];

  const showLegacyKpis = !useRealBilling;

  const focusTabButton = (tabId) => {
    const el = tabButtonRefs.current[tabId];
    if (el && typeof el.focus === 'function') el.focus();
  };

  const handleTabListKeyDown = (event) => {
    const handledKeys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!handledKeys.includes(event.key)) return;

    event.preventDefault();
    const currentIndex = visibleTabs.findIndex((tab) => tab.id === activeTab);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % visibleTabs.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + visibleTabs.length) % visibleTabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = visibleTabs.length - 1;
    }

    const nextTab = visibleTabs[nextIndex];
    if (!nextTab) return;
    selectBillingTab(nextTab.id);
    focusTabButton(nextTab.id);
  };

  const renderActiveTabContent = () => {
    if (activeTab === 'overview') {
      if (useRealBilling) {
        return typeof SaBillingOverview === 'function' ? (
          <SaBillingOverview
            overview={overview}
            loading={ovLoading}
            error={ovError}
            onRetry={refetchOverview}
            onOpenCommercialStatus={() => selectBillingTab('comercial')}
            onOpenSubscriptions={() => selectBillingTab('assinaturas')}
            onOpenInvoices={() => selectBillingTab('faturas')}
          />
        ) : (
          <BillingMissingComponent
            title="Componente de visão geral não carregado"
            scriptPath="components/billing/billing-overview.jsx"
          />
        );
      }
      return <TabBillingOverview overview={overview} />;
    }

    if (activeTab === 'assinaturas') {
      if (useRealBilling) {
        return typeof SaBillingSubscriptions === 'function' ? (
          <SaBillingSubscriptions
            currentUser={currentUser}
            onToast={onToast}
            onBillingChanged={refetchOverview}
          />
        ) : (
          <BillingMissingComponent
            title="Componente de assinaturas não carregado"
            scriptPath="components/billing/billing-subscriptions.jsx"
          />
        );
      }
      return <TabAssinaturas subs={subs} onToast={onToast} />;
    }

    if (activeTab === 'faturas') {
      if (useRealBilling) {
        return typeof SaBillingInvoices === 'function' ? (
          <SaBillingInvoices
            currentUser={currentUser}
            onToast={onToast}
            onBillingChanged={refetchOverview}
          />
        ) : (
          <BillingMissingComponent
            title="Componente de faturas não carregado"
            scriptPath="components/billing/billing-invoices.jsx"
          />
        );
      }
      return <TabFaturas invoices={invs} onToast={onToast} />;
    }

    if (activeTab === 'comercial') {
      return (
        <TabStatusComercial
          currentUser={currentUser}
          onToast={onToast}
          onNav={onNav}
          onBillingChanged={refetchOverview}
        />
      );
    }

    if (!useRealBilling) {
      if (activeTab === 'inadimplencia') {
        return <TabInadimplencia subs={subs} onToast={onToast} onNav={onNav} />;
      }
      if (activeTab === 'trials') {
        return <TabTrials subs={subs} onToast={onToast} />;
      }
      if (activeTab === 'regua') {
        return <TabRegua onToast={onToast} />;
      }
      if (activeTab === 'historico') {
        return <TabHistoricoFinanceiro />;
      }
    }

    return null;
  };

  return (
    <div
      className="content billing-page"
      style={{
        maxWidth: 1200,
        '--text-muted': 'var(--text-secondary)',
        '--brand-red': '#b4234d',
        '--brand-red-600': '#971b3d',
        '--danger': '#b4234d',
      }}
      data-billing-mode={useRealBilling ? 'real' : 'mock'}
    >
      {tenantContext && (
        <div style={{ background:'color-mix(in srgb, var(--info) 8%, var(--bg-elevated))',border:'1px solid color-mix(in srgb, var(--info) 30%, var(--border))',borderRadius:10,padding:'10px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:10 }}>
          <Icon name="cardCoin" size={15} style={{ color:'var(--info)',flexShrink:0 }}/>
          <span style={{ fontSize:13,fontWeight:600 }}>Billing filtrado para {tenantContext.name}</span>
          <code style={{ fontSize:11,fontFamily:'JetBrains Mono, monospace',color:'var(--text-muted)',marginLeft:4 }}>/admin/billing?tenant_id={tenantContext.id}</code>
        </div>
      )}
      <div className="page-header billing-page-header">
        <div>
          <h1>{useRealBilling ? 'Billing' : 'Billing & Inadimplência'}</h1>
          <p className="sub">
            {useRealBilling
              ? 'Gestão interna de assinaturas, faturas e status comercial dos tenants.'
              : 'Assinaturas, faturas, cobrança, trial, MRR e inadimplência dos tenants (protótipo).'}
          </p>
        </div>
        {!useRealBilling && (
          <div className="actions">
            <button className="btn" onClick={() => setShowExport(true)}><Icon name="download" size={13} />Exportar faturas{ultimaExportacao && <span style={{fontSize:10,opacity:.65,marginLeft:6}}>· Última: {ultimaExportacao}</span>}</button>
            <button className="btn" onClick={() => selectBillingTab('regua')}><Icon name="settings" size={13} />Configurar régua</button>
            <button className="btn primary" onClick={() => setShowNovaCobranca(true)}><Icon name="plus" size={13} />Nova cobrança manual</button>
          </div>
        )}
      </div>

      {showLegacyKpis && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, marginBottom: 24 }}>
          {MOCK_KPIS.map(k => (
            <div key={k.label} className="card" style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{k.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: k.color, letterSpacing: '-0.02em', marginBottom: 2 }}>{k.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{k.sub}</div>
            </div>
          ))}
        </div>
      )}

      <div
        className="billing-page-tabs"
        role="tablist"
        aria-label="Seções de Billing"
        onKeyDown={handleTabListKeyDown}
      >
        {visibleTabs.map((tab) => {
          const isSelected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              ref={(el) => { tabButtonRefs.current[tab.id] = el; }}
              type="button"
              role="tab"
              id={`billing-tab-${tab.id}`}
              className={`billing-page-tab${isSelected ? ' is-active' : ''}`}
              aria-selected={isSelected}
              aria-controls={`billing-panel-${tab.id}`}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => selectBillingTab(tab.id)}
            >
              <span className="billing-page-tab-label">{tab.label}</span>
              {tab.prototype && (
                <span className="billing-page-tab-badge">
                  {tab.mockLabel || 'Dados simulados'}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div
        className="billing-page-tabpanel"
        role="tabpanel"
        id={`billing-panel-${activeTab}`}
        aria-labelledby={`billing-tab-${activeTab}`}
        tabIndex={0}
      >
        {renderActiveTabContent()}
      </div>

      {!useRealBilling && (
        <>
          <ExportarFaturasModal
            open={showExport}
            onClose={() => setShowExport(false)}
            onConfirm={(opts) => {
              setShowExport(false);
              setUltimaExportacao('Agora');
              const pLabel = {'7d':'últimos 7 dias','30d':'últimos 30 dias','current':'mês atual','prev':'mês anterior','custom':'período personalizado'}[opts.period] || opts.period;
              onToast(`Exportação ${opts.format} gerada — ${pLabel}`);
            }}
          />
          <NovaCobracaModal
            open={showNovaCobranca}
            onClose={() => setShowNovaCobranca(false)}
            onToast={onToast}
            onAdd={(c) => { setInvs(is => [c, ...is]); selectBillingTab('faturas'); }}
          />
        </>
      )}
    </div>
  );
};

export {
  BillingPage,
};
